import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import { toast } from "react-toastify";
import Topbar from "../../components/Topbar/Topbar";
import Loader from "../../components/Loader";
import { get, post, put } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";

const AddVowReview = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  // edit mode: the clicked row is passed from the Vow Review list
  const isEdit = state?.type === "edit";
  const isView = state?.type === "view";
  const [viewData, setViewData] = useState(null);
  const editData = isView ? viewData : state?.data;
  const prefilled = useRef(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    state: "",
    city: "",
    property: "",
    location: "",
    user: "",
    owner: "",
    rating: 0,
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [optionsList, setOptionsList] = useState({
    states: [],
    cities: [],
    properties: [],
  });

  useEffect(() => {
    getStates();
    if (isView) getDetails();
  }, []);

  // view mode: load the full review from the API
  const getDetails = async () => {
    if (!state?.data?.id) return;
    setLoading(true);
    try {
      const res = await get(`${URLS.VOW_REVIEW_EDIT}${state.data.id}/`);
      if (res?.status === "success") setViewData(res?.data ?? res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // cities of the selected state
  const getCities = async (stateId) => {
    if (!stateId) {
      setOptionsList((prev) => ({ ...prev, cities: [] }));
      return [];
    }
    const res = await get(`${URLS.VOW_REVIEW_CITIES}${stateId}`);
    const cities = res?.status === "success" ? (res?.data ?? res?.cities ?? []) : [];
    setOptionsList((prev) => ({ ...prev, cities }));
    return cities;
  };

  // changing the state clears the city and reloads the city list
  const handleState = (e) => {
    const stateId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      state: stateId,
      city: "",
      property: "",
      location: "",
      owner: "",
    }));
    setErrors((prev) => ({ ...prev, state: "", city: "", property: "" }));
    setOptionsList((prev) => ({ ...prev, properties: [] }));
    getCities(stateId);
  };

  const getStates = async () => {
    const res = await get(URLS.VOW_REVIEW_STATES);
    if (res?.status === "success") {
      setOptionsList((prev) => ({
        ...prev,
        states: res?.data ?? res?.states ?? [],
      }));
    }
  };

  // properties of the selected state and city
  const getProperties = async (stateId, cityId) => {
    if (!stateId || !cityId) {
      setOptionsList((prev) => ({ ...prev, properties: [] }));
      return [];
    }
    const res = await get(
      `${URLS.VOW_REVIEW_PROPERTIES}?state=${stateId}&city=${cityId}`,
    );
    const properties =
      res?.status === "success" ? (res?.data ?? res?.properties ?? []) : [];
    setOptionsList((prev) => ({ ...prev, properties }));
    return properties;
  };

  // changing the city clears the property and reloads the property list
  const handleCity = (e) => {
    const cityId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      city: cityId,
      property: "",
      location: "",
      owner: "",
    }));
    setErrors((prev) => ({ ...prev, city: "", property: "" }));
    getProperties(formData.state, cityId);
  };

  // fill the form from the clicked row once the states are loaded
  useEffect(() => {
    if ((!isEdit && !isView) || !editData || prefilled.current) return;
    if (!optionsList.states.length) return;
    const byName = (list, name) =>
      list.find((item) => item.name === name)?.id;
    prefilled.current = true;
    const stateId = editData.state_id ?? byName(optionsList.states, editData.state);
    (async () => {
      // cities depend on the state and properties on the city, so load them in order
      const cities = await getCities(stateId);
      const cityId = editData.city_id ?? byName(cities, editData.city);
      const properties = await getProperties(stateId, cityId);
      const propertyId =
        (typeof editData.property === "number" ? editData.property : undefined) ??
        editData.property_id ??
        byName(properties, editData.property_name);
      setFormData({
        state: stateId ? String(stateId) : "",
        city: cityId ? String(cityId) : "",
        property: propertyId ? String(propertyId) : "",
        location:
          editData.property_location ??
          [editData.city, editData.state].filter(Boolean).join(", "),
        user: editData.reviewer_name ?? editData.user_name ?? "",
        owner: editData.owner_name ?? "",
        rating: Math.round(Number(editData.rating) || 0),
        description: editData.description ?? "",
      });
    })();
  }, [optionsList, viewData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // picking a property fills its location and owner from the property details API (both stay editable)
  const handleProperty = async (e) => {
    const propertyId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      property: propertyId,
      location: "",
      owner: "",
    }));
    setErrors((prev) => ({ ...prev, property: "" }));
    if (!propertyId) return;

    setLoading(true);
    try {
      const res = await get(URLS.PROPERTY_MASTER + propertyId);
      if (res?.status === "success") {
        const { area_name, city_name, owner_name } = res?.data ?? {};
        setFormData((prev) =>
          // ignore a late response if another property was picked meanwhile
          prev.property !== propertyId
            ? prev
            : {
                ...prev,
                location: [area_name, city_name].filter(Boolean).join(", "),
                owner: owner_name ?? "",
              },
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.state) newErrors.state = "Please select state";
    if (!formData.city) newErrors.city = "Please select city";
    if (!formData.property) newErrors.property = "Please select property";
    if (!formData.user.trim()) newErrors.user = "Please enter user";
    if (!formData.location.trim())
      newErrors.location = "Please enter property location";
    if (!formData.owner.trim()) newErrors.owner = "Please enter owner";
    if (!formData.rating) newErrors.rating = "Please select rating";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = new FormData();
    payload.append("state", formData.state);
    payload.append("city", formData.city);
    payload.append("property", formData.property);
    payload.append("reviewer_name", formData.user.trim());
    payload.append("property_location", formData.location.trim());
    payload.append("owner_name", formData.owner.trim());
    payload.append("rating", formData.rating);
    payload.append("description", formData.description.trim());

    setLoading(true);
    try {
      const res = isEdit
        ? await put(`${URLS.VOW_REVIEW_EDIT}${editData?.id}/`, payload)
        : await post(URLS.VOW_REVIEW_CREATE, payload);
      if (res?.status === "success") {
        toast.success(
          res?.message ||
            (isEdit ? "Review updated successfully" : "Review added successfully"),
        );
        navigate("/review", { state: { tab: "Vow Review" } });
      } else if (res?.status) {
        const fieldErrors = res?.errors
          ? Object.values(res.errors).flat().join(", ")
          : "";
        toast.error(
          res?.message ||
            fieldErrors ||
            (isEdit ? "Failed to update review" : "Failed to add review"),
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Topbar
        header={isView ? "View Review" : isEdit ? "Edit Review" : "Add New Review"}
        back
      />
      <div className="d-flex flex-wrap gap-5">
        <div className="col-md-5">
          <p className="f3 mb-1">
            State <span className="text-danger">*</span>
          </p>
          <select
            name="state"
            value={formData.state}
            onChange={handleState}
            className="input-box"
            disabled={isView}
          >
            <option value="">Select State</option>
            {optionsList.states.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {errors.state && <p className="err">{errors.state}</p>}
        </div>

        <div className="col-md-5">
          <p className="f3 mb-1">
            City <span className="text-danger">*</span>
          </p>
          <select
            name="city"
            value={formData.city}
            onChange={handleCity}
            className="input-box"
            disabled={isView}
          >
            <option value="">Select City</option>
            {optionsList.cities.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {errors.city && <p className="err">{errors.city}</p>}
        </div>

        <div className="col-md-5">
          <p className="f3 mb-1">
            Property <span className="text-danger">*</span>
          </p>
          <select
            name="property"
            value={formData.property}
            onChange={handleProperty}
            className="input-box"
            disabled={isView}
          >
            <option value="">Select Property</option>
            {optionsList.properties.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {errors.property && <p className="err">{errors.property}</p>}
        </div>

        <div className="col-md-5">
          <p className="f3 mb-1">
            Property Location <span className="text-danger">*</span>
          </p>
          <input
            className="input-box"
            disabled={isView}
            name="location"
            value={formData.location}
            onChange={handleChange}
            type="text"
            placeholder="Property Location"
          />
          {errors.location && <p className="err">{errors.location}</p>}
        </div>

        <div className="col-md-5">
          <p className="f3 mb-1">
            User <span className="text-danger">*</span>
          </p>
          <input
            className="input-box"
            disabled={isView}
            name="user"
            value={formData.user}
            onChange={handleChange}
            type="text"
            placeholder="User"
          />
          {errors.user && <p className="err">{errors.user}</p>}
        </div>

        <div className="col-md-5">
          <p className="f3 mb-1">
            Owner <span className="text-danger">*</span>
          </p>
          <input
            className="input-box"
            disabled={isView}
            name="owner"
            value={formData.owner}
            onChange={handleChange}
            type="text"
            placeholder="Owner"
          />
          {errors.owner && <p className="err">{errors.owner}</p>}
        </div>

        <div className="col-md-12">
          <p className="f3 mb-1">Rating</p>
          <div className="d-flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon
                key={star}
                onClick={() => {
                  if (isView) return;
                  setFormData((prev) => ({ ...prev, rating: star }));
                  setErrors((prev) => ({ ...prev, rating: "" }));
                }}
                style={{
                  cursor: isView ? "default" : "pointer",
                  color: star <= formData.rating ? "#FFC107" : "#D1D5DB",
                  fontSize: 30,
                }}
              />
            ))}
          </div>
          {errors.rating && <p className="err">{errors.rating}</p>}
        </div>

        <div className="col-md-12">
          <p className="f3 mb-1">Review</p>
          <textarea
            className="input-box"
            disabled={isView}
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Description"
            style={{ minHeight: 100 }}
          />
        </div>
      </div>

      {!isView && (
      <div className="d-flex ac-je mt-2">
        <button onClick={handleSubmit} className="submit-btn">
          {isEdit ? "Update" : "Add"}
        </button>
      </div>
      )}
      {loading && <Loader />}
    </div>
  );
};

export default AddVowReview;
